/**
 * Two charts, both of things Trellis already counts. Neither draws a trend line: the
 * record holds days, not years, and a line through five points claims a confidence the
 * data does not have. The week is the wider of the two because it is the one that is
 * about today.
 */
import { fmtDate, plural, type DueDay, type OutcomeSegment } from "@trellis/core";
import { Tip } from "./tip.js";

/** What is due between today and a week out, a column per day, the count above each. */
export function DueColumns({ days }: { days: DueDay[] }) {
  const peak = Math.max(1, ...days.map((d) => d.n));
  const total = days.reduce((a, d) => a + d.n, 0);
  const reading = days.map((d, i) => `${d.n} ${i === 0 ? "due today" : `due ${fmtDate(d.date)}`}`).join(", ");
  return (
    <section class="tile chart">
      <div class="chart-head">
        <h2 class="chart-title">
          <Tip what="Open work and cadences due in the next seven days, counted on the date each one is currently due. Today carries anything already past its date, and turns red when it does.">
            Due, next seven days
          </Tip>
        </h2>
        <span class="chart-total num">{total ? `${total} due` : "Nothing due"}</span>
      </div>
      {/* Index 0 is today: `dueWeek` starts there and files everything overdue on it. */}
      <div class="week" role="img" aria-label={reading}>
        {days.map((d, i) => (
          <div key={d.date} class={`col${i === 0 ? " now" : ""}${d.late ? " over" : ""}${d.n === 0 ? " nil" : ""}`}>
            <b class="num">{d.n}</b>
            <i style={`height:${d.n === 0 ? 0 : Math.max(6, (d.n / peak) * 100)}%`} />
          </div>
        ))}
      </div>
      <div class="week-days" aria-hidden="true">
        {days.map((d, i) => <span key={d.date} class={i === 0 ? "now" : ""}>{i === 0 ? "Today" : fmtDate(d.date).slice(0, 3)}</span>)}
      </div>
    </section>
  );
}

/** How the window's closed work turned out, as one bar rather than four numbers. */
export function OutcomeBar({ days, segments }: { days: number; segments: OutcomeSegment[] }) {
  const total = segments.reduce((a, s) => a + s.n, 0);
  return (
    <section class="tile chart">
      <div class="chart-head">
        <h2 class="chart-title">
          <Tip what={`Everything closed in the last ${plural(days, "day")}, split three ways: it landed on the date first accepted, the date moved first and it landed on the new one, or it closed after its date. Open work that is running late is not here; nothing on this bar is unfinished.`}>
            Last {plural(days, "day")}
          </Tip>
        </h2>
        <span class="chart-total num">{total ? `${total} closed` : "Nothing closed"}</span>
      </div>
      {total === 0
        ? <p class="chart-empty">Nothing closed in this window yet.</p>
        : (
          <>
            <div class="bar90" role="img" aria-label={segments.map((s) => `${s.n} ${s.label}`).join(", ")}>
              {segments.filter((s) => s.n > 0).map((s) => (
                <i key={s.key} style={`width:${(s.n / total) * 100}%;background:${s.colour}`} />
              ))}
            </div>
            <ul class="chart-key">
              {segments.map((s) => (
                <li key={s.key}>
                  <span class="swatch" style={`background:${s.colour}`} />
                  <Tip what={s.what}><b class="num">{s.n}</b> {s.label}</Tip>
                </li>
              ))}
            </ul>
          </>
        )}
      <p class="chart-note">Counts of the lines on this page, over {plural(days, "day")}. No trend: the record is days old.</p>
    </section>
  );
}
